import {OrganizationAccount, OrganizationAccountStatus} from '../../accounts/types';
import {PlatformLoader} from '@flexinet/ui-components';
import {
    ActionIcon,
    Badge,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import {AiOutlineDelete, AiOutlineTeam} from 'react-icons/ai';

interface OrganizationMembersTableProps {
    organizationAccounts: OrganizationAccount[];
    isLoading?: boolean;
    onRemoveMember?: (orgAccountId: string) => void;
    canManage?: boolean;
}

const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const OrganizationMembersTable = ({
    organizationAccounts,
    isLoading,
    onRemoveMember,
    canManage = false,
}: OrganizationMembersTableProps) => {

    if (isLoading) {
        return <PlatformLoader message="Loading members..."/>;
    }

    if (!organizationAccounts || organizationAccounts.length === 0) {
        return (
            <Stack align="center" gap="md" py="xl">
                <AiOutlineTeam size={40} color="var(--mantine-color-dimmed)"/>
                <Stack align="center" gap={4}>
                    <Title order={5} c="dimmed">No Members Yet</Title>
                    <Text size="sm" c="dimmed">
                        Add members to your organization using the button above.
                    </Text>
                </Stack>
            </Stack>
        );
    }

    return (
        <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Username</Table.Th>
                    <Table.Th>Account Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Joined</Table.Th>
                    {canManage && <Table.Th>Actions</Table.Th>}
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {organizationAccounts.map((orgAccount) => (
                    <Table.Tr key={orgAccount.id}>
                        <Table.Td>
                            <Text size="sm">{orgAccount.account?.email || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm">{orgAccount.account?.username || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                            <Badge variant="light" color="gray" size="sm">
                                {orgAccount.account?.accountType || '-'}
                            </Badge>
                        </Table.Td>
                        <Table.Td>
                            <Badge
                                variant="light"
                                color={orgAccount.organizationAccountStatus === OrganizationAccountStatus.ACTIVE ? 'green' : 'red'}
                                size="sm"
                            >
                                {orgAccount.organizationAccountStatus || 'ACTIVE'}
                            </Badge>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm" c="dimmed">
                                {formatDate(orgAccount.created)}
                            </Text>
                        </Table.Td>
                        {canManage && (
                            <Table.Td>
                                <Tooltip label={`Remove ${orgAccount.account?.email || 'member'}`} withArrow>
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="sm"
                                        onClick={() => onRemoveMember?.(orgAccount.id)}
                                        aria-label={`Remove ${orgAccount.account?.email || 'member'} from organization`}
                                    >
                                        <AiOutlineDelete size={16}/>
                                    </ActionIcon>
                                </Tooltip>
                            </Table.Td>
                        )}
                    </Table.Tr>
                ))}
            </Table.Tbody>
        </Table>
    );
};
